import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { billingApi } from '../services/billingApi';
import type { PracticeSummary } from '../types/billing';

const PracticesToolbar: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
    <GridToolbarQuickFilter placeholder="Search practices…" debounceMs={200} />
  </Box>
);

const Practices: React.FC = () => {
  const navigate = useNavigate();
  const [practices, setPractices] = useState<PracticeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    billingApi
      .listPractices()
      .then((rows) => {
        if (!cancelled) {
          setPractices(rows);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load practices');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const columns: GridColDef<PracticeSummary>[] = [
    {
      field: 'name',
      headerName: 'Practice',
      flex: 1.5,
      minWidth: 240,
      renderCell: (params: GridRenderCellParams<PracticeSummary>) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.row.name}
          </Typography>
          {params.row.legalName && params.row.legalName !== params.row.name && (
            <Typography variant="caption" color="text.secondary">
              {params.row.legalName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'ein',
      headerName: 'Tax ID',
      width: 130,
      valueGetter: (_value, row) => row.ein || '-',
    },
    {
      field: 'groupNpi',
      headerName: 'Group NPI',
      width: 130,
      valueGetter: (_value, row) => row.groupNpi || '-',
    },
    {
      field: 'locationCount',
      headerName: 'Locations',
      width: 110,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'payersInNetworkCount',
      headerName: 'Payers in network',
      width: 160,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'appsInProgressCount',
      headerName: 'Apps in progress',
      width: 160,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'actionRequiredCount',
      headerName: 'Action required',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<PracticeSummary>) => {
        const value = params.row.actionRequiredCount ?? 0;
        if (!value) {
          return (
            <Typography variant="body2" color="text.secondary">
              0
            </Typography>
          );
        }
        return (
          <Chip
            label={value}
            size="small"
            sx={{ backgroundColor: '#fff3e0', color: '#bf5b00', fontWeight: 700 }}
          />
        );
      },
    },
    {
      field: 'billingCustomer',
      headerName: 'Billing customer',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<PracticeSummary>) =>
        params.row.billingCustomer ? (
          <Chip
            label="Yes"
            size="small"
            sx={{ backgroundColor: '#e3f2fd', color: '#0d47a1', fontWeight: 600 }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            -
          </Typography>
        ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Practices
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Active credentialing engagements, one row per practice. Click a row to view providers, locations, and applications.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid<PracticeSummary>
            rows={practices}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/practices/${params.row.id}`)}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
              sorting: { sortModel: [{ field: 'actionRequiredCount', sort: 'desc' }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{ toolbar: PracticesToolbar }}
            sx={{
              border: 0,
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5', fontWeight: 600 },
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default Practices;
